import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { deleteOnsiteInterview } from '~/actions/PermanentJobs';
import { useAppSelector, useToggle } from '~/hooks';

interface IUseDeleteOnsiteInterviewPayload {
  jobPostingId: string | undefined;
}

/**
 * Delete-flow state for the onsite interview previewer.
 *
 * Two distinct popups:
 *  - `isConfirmOpen`: confirm-before-delete dialog ("Are you sure…") shown
 *    whenever the user has 2+ interviews and clicks the trash icon.
 *  - `isWarningOpen`: informational warning ("Onsite Interview Needed to
 *    Post Job") shown when the user tries to delete their LAST remaining
 *    interview — the deletion is blocked.
 */
export const useDeleteOnsiteInterview = ({ jobPostingId }: IUseDeleteOnsiteInterviewPayload) => {
  const dispatch = useDispatch();
  const [isConfirmOpen, confirmControls] = useToggle();
  const [isWarningOpen, warningControls] = useToggle();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const interviews = useAppSelector((state) => {
    if (!jobPostingId) return [];
    return state.permanentJobs.onsiteInterviews[jobPostingId] ?? [];
  });

  const onDelete = (id: string) => {
    if (!jobPostingId) return;
    if (interviews.length <= 1) {
      warningControls.setOn();
      return;
    }
    setPendingDeleteId(id);
    confirmControls.setOn();
  };

  const onConfirmDelete = () => {
    if (!jobPostingId || !pendingDeleteId) return;
    dispatch(deleteOnsiteInterview({ id: pendingDeleteId, jobPostingId }));
    setPendingDeleteId(null);
    confirmControls.setOff();
  };

  const onCancelDelete = () => {
    setPendingDeleteId(null);
    confirmControls.setOff();
  };

  return {
    isConfirmOpen,
    onDelete,
    onConfirmDelete,
    onCancelDelete,
    isWarningOpen,
    onCloseWarning: warningControls.setOff,
  };
};
